import React from "react";
import { Redirect } from "react-router-dom";

import { EmptyState, PageHeader } from "components/admin";
import { AdministrativeRegionsSection } from "pages/app/workspace/sections/administrative-regions-section";
import { EventsSection } from "pages/app/workspace/sections/events-section";
import { FollowedAthletesSection } from "pages/app/workspace/sections/followed-athletes-section";
import { NotificationsSection } from "pages/app/workspace/sections/notifications-section";
import { OrdersSection } from "pages/app/workspace/sections/orders-section";
import { PaymentsSection } from "pages/app/workspace/sections/payments-section";
import { PlayerClassesSection } from "pages/app/workspace/sections/player-classes-section";
import { PlayerPerformanceSection } from "pages/app/workspace/sections/player-performance-section";
import { PlayerProfileSection } from "pages/app/workspace/sections/player-profile-section";
import { PlayerStatsSection } from "pages/app/workspace/sections/player-stats-section";
import { RequestsSection } from "pages/app/workspace/sections/requests-section";
import { SchoolPlayersSection } from "pages/app/workspace/sections/school-players-section";
import { SchoolTeamsSection } from "pages/app/workspace/sections/school-teams-section";
import { SportAgeGroupsSection } from "pages/app/workspace/sections/sport-age-groups-section";
import { WalletSection } from "pages/app/workspace/sections/wallet-section";
import { PortalSection } from "utils/portal-access";

const SECTION_ROUTE_REDIRECTS: Partial<Record<PortalSection, string>> = {
  school_calendar: "/workspace/schedule?view=calendar",
  school_matches: "/workspace/schedule?view=list",
  school_rosters: "/workspace/schedule?view=list",
  school_roles: "/workspace/school?tab=staff",
  school_schedule: "/workspace/schedule",
  school_scoresheets: "/workspace/schedule?view=list&focus=scoring",
  school_sports: "/workspace/school?tab=programmes",
  schools: "/workspace/school",
};

type WorkspaceSectionContentProps = {
  currentUser: any;
  effectivePortalAccess: any;
  section: PortalSection | null;
};

const WorkspaceSectionFallback: React.FC = () => (
  <>
    <PageHeader
      title="Workspace"
      description="This section is not yet mapped to a dedicated workspace route."
    />
    <EmptyState
      title="Section not configured"
      description="The role-aware navigation is already connected to the API. This section still needs a dedicated screen configuration."
    />
  </>
);

export const WorkspaceSectionContent: React.FC<WorkspaceSectionContentProps> = ({
  currentUser,
  effectivePortalAccess,
  section,
}) => {
  const redirectPath = section ? SECTION_ROUTE_REDIRECTS[section] : undefined;

  if (redirectPath) {
    return <Redirect to={redirectPath} />;
  }

  switch (section) {
    case "notifications":
      return <NotificationsSection />;
    case "orders":
      return <OrdersSection />;
    case "payments":
      return <PaymentsSection />;
    case "wallet":
      return <WalletSection currentUser={currentUser} />;
    case "events":
      return <EventsSection currentUser={currentUser} portalAccess={effectivePortalAccess} />;
    case "player_classes":
      return (
        <PlayerClassesSection currentUser={currentUser} portalAccess={effectivePortalAccess} />
      );
    case "followed_athletes":
      return <FollowedAthletesSection currentUser={currentUser} />;
    case "requests":
      return <RequestsSection currentUser={currentUser} portalAccess={effectivePortalAccess} />;
    case "player_profile":
      return <PlayerProfileSection currentUser={currentUser} />;
    case "player_stats":
      return <PlayerStatsSection currentUser={currentUser} />;
    case "administrative_regions":
      return <AdministrativeRegionsSection portalAccess={effectivePortalAccess} />;
    case "sport_age_groups":
      return <SportAgeGroupsSection portalAccess={effectivePortalAccess} />;
    case "school_teams":
      return <SchoolTeamsSection portalAccess={effectivePortalAccess} />;
    case "school_players":
      return <SchoolPlayersSection portalAccess={effectivePortalAccess} />;
    case "player_performance_records":
      return (
        <PlayerPerformanceSection currentUser={currentUser} portalAccess={effectivePortalAccess} />
      );
    default:
      return <WorkspaceSectionFallback />;
  }
};

export default WorkspaceSectionContent;
